import { forwardRef } from 'react';
import type {
  CSSProperties,
  ElementType,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';
import { kaiFontFamily, kaiFontWeight } from '../../../core/tokens';
import { kaiTypographyWeb, pxToRem } from '../../typography';
import { adminCommonColors } from './adminCommonColors';
import { resolveStyleOverride, type StyleOverride } from './styleOverrides';

export type CommonFieldState = 'default' | 'focused' | 'error' | 'changed' | 'locked' | 'disabled';

type CommonFieldContainerProps = HTMLAttributes<HTMLElement> & {
  as?: ElementType;
  styleOverride?: StyleOverride;
};

type CommonFieldInputControlProps = InputHTMLAttributes<HTMLInputElement> & {
  styleOverride?: StyleOverride;
};

export type CommonFieldProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
  styleOverride?: StyleOverride;
  label: ReactNode;
  value?: ReactNode;
  state?: CommonFieldState;
  helperText?: ReactNode;
  inputProps?: CommonFieldInputControlProps;
  boxCss?: StyleOverride;
  labelCss?: StyleOverride;
  valueCss?: StyleOverride;
  helperCss?: StyleOverride;
};

const figmaWhite = adminCommonColors.surface;
const figmaCharcoalText = adminCommonColors.text;

type CommonFieldStateStyle = {
  box: CSSProperties;
  labelColor: string;
  valueColor: string;
  valueFontWeight?: CSSProperties['fontWeight'];
  valueLineHeight: string;
  helperTextColor: string;
};

const COMMON_FIELD_STATE_STYLES: Record<CommonFieldState, CommonFieldStateStyle> = {
  default: {
    box: { background: figmaWhite, border: `1px solid ${adminCommonColors.textTertiary}` },
    labelColor: adminCommonColors.textTertiary,
    valueColor: figmaCharcoalText,
    valueLineHeight: '1.33',
    helperTextColor: adminCommonColors.textTertiary,
  },
  focused: {
    box: { background: figmaWhite, border: `2px solid ${adminCommonColors.borderFocus}` },
    labelColor: adminCommonColors.textTertiary,
    valueColor: figmaCharcoalText,
    valueLineHeight: '1.33',
    helperTextColor: adminCommonColors.textTertiary,
  },
  error: {
    box: { background: figmaWhite, border: `1px solid ${adminCommonColors.error}` },
    labelColor: adminCommonColors.error,
    valueColor: figmaCharcoalText,
    valueLineHeight: '1.33',
    helperTextColor: adminCommonColors.error,
  },
  changed: {
    box: { background: figmaWhite, border: `1px solid ${adminCommonColors.border}` },
    labelColor: adminCommonColors.textTertiary,
    valueColor: adminCommonColors.text,
    valueLineHeight: '1.167',
    helperTextColor: adminCommonColors.textSecondary,
  },
  locked: {
    box: { background: adminCommonColors.surfaceMuted, border: 0 },
    labelColor: adminCommonColors.textTertiary,
    valueColor: adminCommonColors.text,
    valueFontWeight: kaiFontWeight.bold,
    valueLineHeight: '1.167',
    helperTextColor: adminCommonColors.textTertiary,
  },
  disabled: {
    box: { background: adminCommonColors.surfaceMuted, border: 0 },
    labelColor: adminCommonColors.textTertiary,
    valueColor: adminCommonColors.textTertiary,
    valueLineHeight: '1.33',
    helperTextColor: adminCommonColors.textTertiary,
  },
};

const COMMON_FIELD_ROOT_STYLE: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  background: 'transparent',
  boxSizing: 'border-box',
};

const COMMON_FIELD_BOX_STYLE: CSSProperties = {
  position: 'relative', // anchors the floating label
  marginTop: pxToRem(8), // reserves room above the box for the label to float into
  width: '100%',
  boxSizing: 'border-box',
  paddingTop: pxToRem(15),
  paddingBottom: pxToRem(14),
  paddingLeft: pxToRem(9),
  paddingRight: pxToRem(9),
};

const COMMON_FIELD_LABEL_STYLE: CSSProperties = {
  position: 'absolute',
  left: pxToRem(8),
  top: pxToRem(-8), // half this label's own rendered line-height (10px * 1.6 / 2) - floats it over the box's top border
  zIndex: 1,
  margin: 0,
  padding: `0 ${pxToRem(2)}`,
  background: figmaWhite,
  fontFamily: kaiFontFamily,
  fontSize: pxToRem(10),
  fontWeight: kaiFontWeight.regular,
  fontStyle: 'normal',
  lineHeight: 1.6, // matches this block's 10px font-size (16px / 10px)
  letterSpacing: '0.2px',
  whiteSpace: 'nowrap',
};

const COMMON_FIELD_VALUE_STYLE: CSSProperties = {
  display: 'block',
  width: '100%',
  margin: 0,
  padding: 0,
  border: 0,
  background: 'transparent',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: kaiFontFamily,
  fontSize: pxToRem(12),
  fontStyle: 'normal',
  fontWeight: kaiFontWeight.regular,
  letterSpacing: '0.24px',
  whiteSpace: 'nowrap',
};

const COMMON_FIELD_HELPER_TEXT_STYLE: CSSProperties = {
  marginLeft: pxToRem(-1),
  width: '100%',
  padding: 0,
  fontFamily: kaiFontFamily,
  fontSize: pxToRem(10),
  fontStyle: 'normal',
  fontWeight: kaiFontWeight.regular,
  lineHeight: 1.4, // matches this block's 10px font-size (14px / 10px)
  letterSpacing: '0.2px',
};

const COMMON_FIELD_TEXT_STYLE: CSSProperties = {
  width: '100%',
  border: 0,
  padding: 0,
  background: 'transparent',
  outline: 'none',
  boxSizing: 'border-box',
  ...kaiTypographyWeb.xSmall,
  letterSpacing: '0.24px',
};

const COMMON_FIELD_TEXTAREA_STYLE: CSSProperties = {
  ...COMMON_FIELD_TEXT_STYLE,
  resize: 'none',
  minHeight: 16,
};

const createCommonFieldContainer = (
  defaultAs: ElementType,
  displayName: string,
  baseStyle?: CSSProperties,
) => {
  const Component = forwardRef<HTMLElement, CommonFieldContainerProps>(
    ({ as, styleOverride: fieldCss, style, ...props }, ref) => {
      const Tag = as ?? defaultAs;

      return (
        <Tag
          ref={ref}
          style={{
            ...baseStyle,
            ...style,
            ...resolveStyleOverride(fieldCss),
          }}
          {...props}
        />
      );
    },
  );

  Component.displayName = displayName;
  return Component;
};

export const CommonField = forwardRef<HTMLDivElement, CommonFieldProps>(
  (
    {
      styleOverride: fieldCss,
      style,
      label,
      value,
      state = 'default',
      helperText,
      inputProps,
      boxCss,
      labelCss,
      valueCss,
      helperCss,
      ...props
    },
    ref,
  ) => {
    const stateStyle = COMMON_FIELD_STATE_STYLES[state];
    const {
      styleOverride: inputCss,
      style: inputStyle,
      disabled: inputDisabled,
      readOnly: inputReadOnly,
      ...nativeInputProps
    } = inputProps ?? {};
    const valueStyle = {
      ...COMMON_FIELD_VALUE_STYLE,
      color: stateStyle.valueColor,
      fontWeight: stateStyle.valueFontWeight ?? kaiFontWeight.regular,
      lineHeight: stateStyle.valueLineHeight,
    };

    return (
      <div
        ref={ref}
        style={{
          ...COMMON_FIELD_ROOT_STYLE,
          ...style,
          ...resolveStyleOverride(fieldCss),
        }}
        data-kai-shiki-common-field-state={state}
        {...props}
      >
        <div
          data-kai-shiki-common-field-box='true'
          style={{
            ...COMMON_FIELD_BOX_STYLE,
            ...stateStyle.box,
            ...resolveStyleOverride(boxCss),
          }}
        >
          <div
            data-kai-shiki-common-field-label='true'
            style={{
              ...COMMON_FIELD_LABEL_STYLE,
              color: stateStyle.labelColor,
              ...resolveStyleOverride(labelCss),
            }}
          >
            {label}
          </div>
          {inputProps
            ? (
              <input
                {...nativeInputProps}
                disabled={state === 'disabled' || inputDisabled}
                readOnly={state === 'locked' || inputReadOnly}
                data-kai-shiki-common-field-value='true'
                style={{
                  ...valueStyle,
                  ...inputStyle,
                  ...resolveStyleOverride(inputCss),
                  ...resolveStyleOverride(valueCss),
                }}
              />
            )
            : (
              <div
                data-kai-shiki-common-field-value='true'
                style={{
                  ...valueStyle,
                  ...resolveStyleOverride(valueCss),
                }}
              >
                {value}
              </div>
            )}
        </div>
        {helperText && (
          <div
            data-kai-shiki-common-field-helper='true'
            style={{
              ...COMMON_FIELD_HELPER_TEXT_STYLE,
              color: stateStyle.helperTextColor,
              ...resolveStyleOverride(helperCss),
            }}
          >
            {helperText}
          </div>
        )}
      </div>
    );
  },
);

CommonField.displayName = 'CommonField';

// Web-only admin primitive extracted from renovator CommonField without Emotion.
export const CommonFieldShell = createCommonFieldContainer('div', 'CommonFieldShell');

export const CommonFieldLabel = createCommonFieldContainer('div', 'CommonFieldLabel');

export const CommonFieldText = createCommonFieldContainer(
  'div',
  'CommonFieldText',
  COMMON_FIELD_TEXT_STYLE,
);

export const CommonFieldAdornment = createCommonFieldContainer('div', 'CommonFieldAdornment');

export type CommonFieldInputProps = InputHTMLAttributes<HTMLInputElement> & { styleOverride?: StyleOverride };

export const CommonFieldInput = forwardRef<HTMLInputElement, CommonFieldInputProps>(
  ({ styleOverride: fieldCss, style, ...props }, ref) => (
    <input
      ref={ref}
      style={{
        ...COMMON_FIELD_TEXT_STYLE,
        ...style,
        ...resolveStyleOverride(fieldCss),
      }}
      {...props}
    />
  ),
);

CommonFieldInput.displayName = 'CommonFieldInput';

export type CommonFieldTextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  styleOverride?: StyleOverride;
};

export const CommonFieldTextarea = forwardRef<HTMLTextAreaElement, CommonFieldTextareaProps>(
  ({ styleOverride: fieldCss, style, ...props }, ref) => (
    <textarea
      ref={ref}
      style={{
        ...COMMON_FIELD_TEXTAREA_STYLE,
        ...style,
        ...resolveStyleOverride(fieldCss),
      }}
      {...props}
    />
  ),
);

CommonFieldTextarea.displayName = 'CommonFieldTextarea';
